import type { ConfigurationValidationError } from '@festina/configuration-general-types/configuration'
import { LayerKey, type WorkspaceKey } from '@festina-life/api'
import { assertNotEmpty, type Optional } from '@festina-life/core'
import { useMemo } from 'react'

import { useMetaModelContext } from '../../context/metaModelContext'
import type { ValidationErrorWithLocation } from '../errors/types'
import { isWorkspaceEnumDefinitionOperation } from '../templateOperations/types'
import { isOperationValidationError } from '../types'
import { useWorkspaceOperations } from '../workspace/useWorkspaceOperations'
import type { EnumDefinitionWithoutLiterals } from './types'

export const useDraftEnumDefinitionsWithoutLiterals = (
  workspaceKey: Optional<WorkspaceKey>,
  errors: ValidationErrorWithLocation<ConfigurationValidationError>[],
): EnumDefinitionWithoutLiterals[] => {
  const { data: draftOperations } = useWorkspaceOperations(
    workspaceKey,
    isWorkspaceEnumDefinitionOperation,
  )
  const { blockTopology } = useMetaModelContext()
  const rootBlock = blockTopology.name

  return useMemo(() => {
    // We intentionally preserve old fetch data in the useFetchWorkspaceOperations hook
    // in order to make the live update experience nicer for the user. Therefore, we need
    // to check whether we're in a workspace and return [] if not, since in that case
    // the data in draftOperations is irrelevant.
    if (!workspaceKey) {
      return []
    }

    return draftOperations.map((workspaceOperation) => {
      assertNotEmpty(workspaceOperation.operation.entity?.name)

      return {
        name: workspaceOperation.operation.entity?.name,
        internalOperation: workspaceOperation.operation,
        changesetHistory: undefined,
        isDraft: true,
        layer: new LayerKey({
          tenantId: workspaceOperation.tenantId,
          layerId: workspaceOperation.layer,
        }),
        annulOperations: [], // Draft definitions cannot be annulled, since they are not yet part of the configuration. Therefore, this array will always be empty.
        errors: (errors ?? []).filter(
          ({ error }) =>
            isOperationValidationError(error) &&
            error.operationKey === workspaceOperation.operation.operationKey,
        ),
        actions: [],
        block: workspaceOperation.block ?? rootBlock,
      } satisfies EnumDefinitionWithoutLiterals
    })
  }, [draftOperations, errors, workspaceKey, rootBlock])
}
